import { BenefitType } from '@wix/ambassador-pricing-plans-v2-benefit/types';
import { ServiceOptionType } from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';
import { PriceUtils } from '@wix/bookings-uou-mappers';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import { ExperimentsConsts } from '../../../../consts/experiments';
import React, { FC, useEffect, useState } from 'react';
import {
  Dropdown,
  DropdownOptionProps,
  DropdownTheme,
} from 'wix-ui-tpa/cssVars';
import { BorderStyle } from '../../../../types/types';
import { getServiceSlotIdentifier, mapToArray } from '../../../../utils';
import { getContent } from '../../../../utils/content/content';
import { getMaxNumberOfParticipants } from '../../../../utils/numberOfParticipant';
import {
  getParticipantRangeVariant,
  isLocationOption,
  isRuleBasedOption,
} from '../../../../utils/mappers/dynamic-price.mapper';
import { ServiceData, SlotService } from '../../../../utils/state/types';
import { useFormActions } from '../../Hooks/useFormActions';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';
import { useSettingsAdapter } from '../../Settings/Utils/SettingsAdapter';
import settingsParams from '../../settingsParams';
import Text, { TextType } from '../../Text/Text';
import { NumberOfParticipantsDropdownDataHooks } from './datahooks';
import { classes, st } from './NumberOfParticipantsDropdown.st.css';

export interface NumberOfParticipantsDropdownProps {
  serviceData: ServiceData;
  initialSelectedId?: string;
}

const createDropdownOptions = (
  maxNumberOfParticipants: number,
  getOptionPrice?: (numberOfParticipants: number) => string | undefined,
): DropdownOptionProps[] => {
  return Array.from({ length: maxNumberOfParticipants }, (_, index) => {
    const numberOfParticipants = index + 1;
    const optionPrice = getOptionPrice?.(numberOfParticipants);
    return {
      id: numberOfParticipants.toString(),
      value: numberOfParticipants.toString(),
      isSelectable: true,
      ...(optionPrice
        ? {
            suffix: (
              <Text
                type={TextType.Secondary}
                className={classes.optionPrice}
                data-hook={NumberOfParticipantsDropdownDataHooks.OPTION_PRICE}
              >
                {optionPrice}
              </Text>
            ),
          }
        : {}),
    };
  });
};

const NumberOfParticipantsDropdown: FC<NumberOfParticipantsDropdownProps> = ({
  serviceData,
  initialSelectedId,
}) => {
  const singleService = mapToArray<SlotService>(serviceData.slotServices)[0];
  const { setNumberOfParticipants } = useFormActions();

  const maxNumberOfParticipants = getMaxNumberOfParticipants(
    serviceData,
    getServiceSlotIdentifier(singleService.nestedSlot),
  );

  const settings = useSettingsAdapter();

  const label = getContent({
    settings,
    settingsParam: settingsParams.numberOfParticipantsDropdownLabel,
  });

  const theme =
    settings.get(settingsParams.fieldsBorderStyle) === BorderStyle.UNDERLINE
      ? DropdownTheme.Line
      : DropdownTheme.Box;

  const { isMobile } = useEnvironment();

  const [selectedId, setSelectedId] = useState<string | undefined>(
    initialSelectedId || '1',
  );

  const { isVariedPricing } = singleService!.service.payment.paymentDetails;
  const serviceOptionsAndVariants =
    singleService?.dynamicPriceInfo?.serviceOptionsAndVariants;
  const doesServiceOptionsAndVariantsExists =
    singleService && !!serviceOptionsAndVariants;
  const { experiments } = useExperiments();
  const isRuleBasedPricing =
    experiments.enabled(
      ExperimentsConsts.SupportDateAndNumOfParticipantsVariantsUoU,
    ) && isRuleBasedOption(serviceOptionsAndVariants?.options);
  const isLocationPricing = isLocationOption(
    serviceOptionsAndVariants?.options,
  );
  const shouldNotRenderDropdown =
    doesServiceOptionsAndVariantsExists &&
    isVariedPricing &&
    !isRuleBasedPricing &&
    !isLocationPricing;

  const { t } = useTranslation();
  const { dateRegionalSettingsLocale } = useFormComponentContext();

  const isNumberOfParticipantsPricing =
    isRuleBasedPricing &&
    serviceOptionsAndVariants?.options?.values?.[0]?.type ===
      ServiceOptionType.NUMBER_OF_PARTICIPANTS;

  const getOptionPrice = (numberOfParticipants: number): string | undefined => {
    if (!isNumberOfParticipantsPricing) {
      return undefined;
    }
    const variant = getParticipantRangeVariant(
      serviceOptionsAndVariants,
      numberOfParticipants,
    );
    if (!variant?.price?.value) {
      return undefined;
    }
    const price = PriceUtils.getFormattedCurrency({
      price: Number(variant.price.value),
      currency: variant.price.currency!,
      locale: dateRegionalSettingsLocale,
    });
    const rangeName = variant.choices?.[0]?.participantRange?.name;
    return rangeName
      ? t('app.dynamic-price.number-of-participants.option-price-with-name', {
          price,
          rangeName,
        })
      : t('app.dynamic-price.number-of-participants.option-price', { price });
  };

  const dropdownOptions = createDropdownOptions(
    maxNumberOfParticipants,
    getOptionPrice,
  );

  const { selectedPaymentOption } = singleService;

  const isPlanUnlimited =
    selectedPaymentOption?.benefitInfo?.benefit?.benefitType ===
    BenefitType.UNLIMITED;

  useEffect(() => {
    if (isPlanUnlimited && selectedId) {
      setSelectedId('1');
    }
  }, [isPlanUnlimited]);

  if (dropdownOptions.length === 1 || shouldNotRenderDropdown) {
    return null;
  }

  return (
    <div
      className={st(classes.root, {
        theme,
        isMobile,
      })}
    >
      <Dropdown
        data-hook={NumberOfParticipantsDropdownDataHooks.DROPDOWN}
        key={selectedId}
        upgrade
        className={classes.dropdown}
        theme={theme}
        options={dropdownOptions}
        disabled={isPlanUnlimited}
        readOnly={isPlanUnlimited}
        initialSelectedId={selectedId}
        onChange={(option) => {
          setNumberOfParticipants(
            Number(option.value),
            getServiceSlotIdentifier(singleService.nestedSlot),
          );
          setSelectedId(option.id!);
        }}
        label={label}
        mobileNativeSelect={isMobile}
      />
    </div>
  );
};
export default NumberOfParticipantsDropdown;
